import React, { useState } from 'react';
import { Icon, Slider, Tooltip } from '@kontentino/ui';
import clsx from 'clsx';
import { animated, useSpring } from '@react-spring/web';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faMusic } from '@fortawesome/pro-regular-svg-icons/faMusic';
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';
import type { AudioMixVolumes } from '../hooks/useVolumeDraft';
import InstagramAudioDeviceNotice from './InstagramAudioDeviceNotice';

interface InstagramAudioVolumeControlsProps {
  volumes: AudioMixVolumes;
  hasVideo: boolean;
  isDisabled?: boolean;
  onDrag: (volumes: AudioMixVolumes) => void;
  onCommit: (volumes: AudioMixVolumes) => void;
}

interface VolumeRowProps {
  icon: IconDefinition;
  label: string;
  value: number;
  disabled?: boolean;
  onDrag: (value: number) => void;
  onCommit: (value: number) => void;
  dataCy: string;
}

const VolumeRow = ({
  icon,
  label,
  value,
  disabled,
  onDrag,
  onCommit,
  dataCy,
}: VolumeRowProps) => (
  <div
    className={clsx('tw-flex tw-flex-col tw-gap-2', {
      'tw-opacity-60': disabled,
    })}
  >
    <div className="tw-flex tw-items-center tw-gap-2">
      <Icon icon={icon} size="sm" className="tw-text-grayscale-140" />
      <span className="tw-flex-1 tw-select-none tw-text-sm tw-font-medium tw-text-grayscale-180">
        {label}
      </span>
      <span className="tw-text-xs tw-tabular-nums tw-text-grayscale-140">
        {value}%
      </span>
    </div>
    <Slider
      value={[value]}
      onValueChange={([next]) => onDrag(next)}
      onValueCommit={([next]) => onCommit(next)}
      min={0}
      max={100}
      step={1}
      disabled={disabled}
      tooltip={`${value}%`}
      data-cy={dataCy}
    />
  </div>
);

const InstagramAudioVolumeControls = ({
  volumes,
  hasVideo,
  isDisabled,
  onDrag,
  onCommit,
}: InstagramAudioVolumeControlsProps) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const contentSpring = useSpring({
    opacity: isCollapsed ? 0 : 1,
    gridTemplateRows: isCollapsed ? '0fr' : '1fr',
    marginTop: isCollapsed ? 0 : 4,
    config: { tension: 300, friction: 30 },
  });

  const videoRow = (
    <VolumeRow
      icon={faVideo}
      label={t('instagramAudio.videoVolume')}
      value={volumes.videoVolume}
      disabled={isDisabled || !hasVideo}
      onDrag={(videoVolume) => onDrag({ ...volumes, videoVolume })}
      onCommit={(videoVolume) => onCommit({ ...volumes, videoVolume })}
      dataCy="instagram-audio_video-volume-slider"
    />
  );

  return (
    <div className="tw-border-t tw-border-grayscale-10 tw-pt-4">
      <div
        className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-text-sm tw-font-medium tw-text-grayscale-180"
        onClick={() => setIsCollapsed((prev) => !prev)}
        data-cy="instagram-audio_volume-controls-toggle"
      >
        <span className="tw-select-none">
          {t('instagramAudio.volumeBalance')}
        </span>
        <Icon
          icon={faChevronDown}
          size="sm"
          className={clsx('tw-transition-transform tw-duration-200', {
            'tw--rotate-90': isCollapsed,
          })}
        />
      </div>

      <animated.div style={contentSpring} className="tw-grid">
        <div className="tw--mx-2 tw-min-h-0 tw-overflow-clip tw-px-2">
          <div className="tw-flex tw-flex-col tw-gap-4 tw-py-3">
            <VolumeRow
              icon={faMusic}
              label={t('instagramAudio.audioVolume')}
              value={volumes.audioVolume}
              disabled={isDisabled}
              onDrag={(audioVolume) => onDrag({ ...volumes, audioVolume })}
              onCommit={(audioVolume) => onCommit({ ...volumes, audioVolume })}
              dataCy="instagram-audio_audio-volume-slider"
            />

            {hasVideo ? (
              videoRow
            ) : (
              <Tooltip content={t('instagramAudio.videoVolumeNoVideo')}>
                <span className="tw-block">{videoRow}</span>
              </Tooltip>
            )}

            <InstagramAudioDeviceNotice />
          </div>
        </div>
      </animated.div>
    </div>
  );
};

export default InstagramAudioVolumeControls;
